<template>
  <!-- backdrop-dismiss stays false even for optional surveys: dismissal happens
       only through explicit buttons (Not now / Don't ask again), because the
       BACKEND owns all pacing — a silent local dismissal would just re-pop on
       the next fetch. The modal closes by itself when the queue drains. -->
  <ion-modal
    :is-open="showSurvey"
    css-class="survey-prompt-modal"
    :backdrop-dismiss="false"
    :can-dismiss="canDismiss"
    @did-dismiss="onDidDismiss"
  >
    <!-- Keyed by token: advancing to the next survey remounts the child with
         fresh answer state without closing/reopening the sheet. -->
    <SurveyPromptModal
      v-if="store.current"
      :key="store.current.token"
      :survey="store.current"
      :pending="store.submitting"
      :error="store.respondError"
      :field-errors="store.fieldErrors"
      @answer="(a) => store.respond('answer', a)"
      @agree="(a) => store.respond('agree', a)"
      @decline="() => store.respond('decline')"
      @snooze="() => store.respond('snooze')"
      @opt-out="() => store.respond('opt_out')"
    />
  </ion-modal>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { IonModal } from '@ionic/vue';
import { useRoute } from 'vue-router';
import SurveyPromptModal from '@/components/modals/SurveyPromptModal.vue';
import { useSurveysStore } from '@/stores/surveys.store';
import { useSessionStore } from '@/stores/session.store';
import { isPublicRouteName } from '@/router';

/**
 * The always-mounted survey popup host. Lives in App.vue (NOT TabsLayout) so a
 * REQUIRED survey covers the standalone authed pages (/forms, /notes, /leaves…)
 * too — a tabs-only host could be escaped by navigating to any of them.
 *
 * Display guard: authenticated + not on a public route. The route check is what
 * keeps the popup off onboarding/sign-in — and therefore away from the
 * company-key and MFA modals, which only exist on the sign-in page.
 *
 * Fetch triggers (each fetch bills an impression server-side — see the store):
 *  - the auth transition (login, MFA completion, boot restore) → forced fetch;
 *  - return to foreground → throttled fetch (token-refresh.ts precedent);
 *  - after each respond the store force re-fetches on its own.
 */
const store = useSurveysStore();
const sessionStore = useSessionStore();
const route = useRoute();

const showSurvey = computed(
  () => sessionStore.isAuthenticated && !isPublicRouteName(route.name) && !!store.current,
);

watch(
  () => sessionStore.isAuthenticated,
  (authed) => {
    if (authed) void store.fetchPending({ force: true });
  },
  { immediate: true },
);

/**
 * Ionic's Android hardware-back handler dismisses the top overlay REGARDLESS of
 * backdrop-dismiss (only canDismiss blocks it) — without this, the back button
 * would escape a required survey and, because is-open stays true, the modal
 * could never re-present for the rest of the session. Dismissal is allowed only
 * once the queue has drained (which is also how the programmatic close works).
 */
const canDismiss = (): boolean => !store.current;

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible' && sessionStore.isAuthenticated) {
    void store.fetchPending();
  }
}

onMounted(() => document.addEventListener('visibilitychange', onVisibilityChange));
onUnmounted(() => document.removeEventListener('visibilitychange', onVisibilityChange));

/** Programmatic closes only (the queue drained) — never a write to the server. */
function onDidDismiss(): void {
  /* no-op: state is derived, dismissal decisions live in the modal's buttons */
}
</script>
